import { copyTextToClipboard } from '@lib/clipboard'
import React, { useEffect, useRef, useState } from 'react'
import { toast } from 'react-hot-toast'
import { CopyControl } from './CopyButton.styles'

/** How long the button reads "Copied" after a successful copy. */
export const COPIED_MS = 1500

interface CopyButtonProps {
	/** The text placed on the clipboard when the button is pressed. */
	value: string
	/** Resting label; the button reads "Copied" for a moment after a successful copy. */
	label?: string
	/** Toast shown when the clipboard refuses — say what could not be copied. */
	errorMessage?: string
	disabled?: boolean
	/** Native tooltip, typically the reason the button is disabled. */
	title?: string
	/** Lets a call site restyle the button through `styled(CopyButton)`. */
	className?: string
}

/**
 * The copy control inside a CopyField: one press copies `value` and the label
 * flips to "Copied" and back, so success is confirmed in place rather than by a
 * toast. Owns the clipboard call and the revert timer, and surfaces only failure
 * as a toast. Internal to this folder — call sites use CopyField.
 */
const CopyButton: React.FC<CopyButtonProps> = ({
	value,
	label = 'Copy',
	errorMessage = 'Could not copy. Please copy it manually.',
	disabled,
	title,
	className,
}) => {
	const [copied, setCopied] = useState(false)
	const revertTimer = useRef<ReturnType<typeof setTimeout>>()

	useEffect(() => () => clearTimeout(revertTimer.current), [])

	const handleCopy = async () => {
		try {
			await copyTextToClipboard(value)
			setCopied(true)
			clearTimeout(revertTimer.current)
			revertTimer.current = setTimeout(() => setCopied(false), COPIED_MS)
		} catch (error) {
			toast.error(errorMessage)
			console.error('Error copying to clipboard:', error)
		}
	}

	return (
		<CopyControl type="button" onClick={handleCopy} disabled={disabled} title={title} className={className}>
			{copied ? 'Copied' : label}
		</CopyControl>
	)
}

export default CopyButton
